// FILE: src/components/PaceBreakPanel.js
//
// Break coaching panel (Phase 4). Rendered inside the ExamBreak overlay during
// a scheduled break, it gives the learner a calm, fuller read of their pacing —
// the one place pace coaching is allowed to be expansive, since the learner has
// chosen to pause. Presentational: it renders a paceService snapshot captured
// at break entry (the exam timer is paused, so the numbers are stable).
//
// Design: docs/pace-coaching-proposal.md §4b.

import React from 'react';
import css from './PaceBreakPanel.module.css';
import { PaceChip } from './PaceCard';
import { paceRecommendationKey } from '../../services/paceService';
import { PACE_MESSAGES } from '../../config/paceMessages';

/** Format a duration using hours when >= 60 min (e.g. "42 min", "1h 02m"). */
function fmtMins(totalSeconds) {
  const m = Math.max(0, Math.round((totalSeconds || 0) / 60));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm === 0 ? `${h}h` : `${h}h ${String(mm).padStart(2, '0')}m`;
}

function Row({ label, children }) {
  return (
    <div className={css.row}>
      <span className={css.label}>{label}</span>
      <span className={css.value}>{children}</span>
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} props.snapshot          paceService.computePace() at break entry
 * @param {number} props.elapsedSeconds     exam time elapsed so far
 * @param {number} props.remainingSeconds   exam time remaining
 * @param {string} [props.personalizationTag] dominant derivePaceProfile tag, if any
 */
export default function PaceBreakPanel({ snapshot, elapsedSeconds, remainingSeconds, personalizationTag }) {
  if (!snapshot || !snapshot.valid) return null;

  const { answeredCount, totalQuestions, reviewBufferSeconds, projectedUnanswered } = snapshot;
  const unanswered = Math.round(projectedUnanswered);
  const forecast =
    unanswered >= 1
      ? `At this pace, about ${unanswered} ${unanswered === 1 ? 'question' : 'questions'} may be left unanswered.`
      : reviewBufferSeconds > 30
        ? `At this pace, you'll finish with about ${fmtMins(reviewBufferSeconds)} for review.`
        : "At this pace, you'll finish with little time to spare for review.";

  const recommendation = PACE_MESSAGES.break[paceRecommendationKey(snapshot)];

  return (
    <div className={css.panel} data-testid="pace-break-panel">
      <p className={css.title}>Your pace so far</p>
      <div className={css.rows}>
        <Row label="Questions completed">{answeredCount} of {totalQuestions}</Row>
        <Row label="Time elapsed">{fmtMins(elapsedSeconds)}</Row>
        <Row label="Time remaining">{fmtMins(remainingSeconds)}</Row>
        <Row label="Current pace"><PaceChip snapshot={snapshot} /></Row>
      </div>
      <p className={css.forecast}>{forecast}</p>
      <div className={css.rec} data-testid="pace-break-recommendation">{recommendation}</div>
      {personalizationTag && PACE_MESSAGES.personalization[personalizationTag] && (
        <div className={css.tip} data-testid="pace-break-personalization">
          {PACE_MESSAGES.personalization[personalizationTag]}
        </div>
      )}
    </div>
  );
}
